import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SyncProblemOutlinedIcon from "@mui/icons-material/SyncProblemOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { forwardRef, createElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";

import type { SvgIconProps } from "@mui/material";
import type { ComponentType } from "react";

/**
 * Curated catalog of design-system icons.
 *
 * Source of truth for which Material icon we use for a given semantic
 * concept. Reach for icons by their semantic name (e.g. `view`) rather
 * than importing directly from `@mui/icons-material`, so we can swap
 * the underlying glyph in one place if the design changes.
 *
 * Internal: not exported from the public barrel. Consumers use `<Icon />`
 * and the `IconName` type. See README for tree-shaking rationale.
 */
const icons = {
  // Actions / CRUD
  add: AddIcon,
  edit: EditIcon,
  delete: DeleteOutlineIcon,
  /** Non-destructive removal (e.g. exclude, clear flag). Distinct from `delete`. */
  remove: RemoveCircleOutlineOutlinedIcon,
  copy: ContentCopyIcon,
  refresh: RefreshIcon,
  sync: SyncIcon,
  /** Reassign an item to a different parent/location. */
  move: SyncAltIcon,
  /** Publish or export a configured set of changes. */
  publishChanges: PublishedWithChangesOutlinedIcon,
  close: CloseIcon,
  check: CheckIcon,

  // Visibility / state
  view: VisibilityOutlinedIcon,
  highlight: OutlinedFlagIcon,

  // Files / docs
  upload: UploadFileIcon,
  download: DownloadOutlinedIcon,
  document: DescriptionOutlinedIcon,

  // Navigation
  back: ArrowBackIcon,
  forward: ArrowForwardIcon,
  expand: ExpandMoreIcon,
  collapse: ExpandLessIcon,
  chevronRight: ChevronRightIcon,
  /** Link out to an external destination (opens in new tab). */
  external: LaunchIcon,
  more: MoreVertIcon,

  // Status / signals
  success: CheckCircleOutlineOutlinedIcon,
  warning: WarningAmberOutlinedIcon,
  error: ErrorOutlineOutlinedIcon,
  info: InfoOutlinedIcon,
  help: HelpOutlineOutlinedIcon,
  /**
   * Marks a value someone changed by hand after it was filled in for them —
   * the "Edited" notation. `edit` is the action that starts a change.
   */
  edited: EditOutlinedIcon,
  /** Undo an edit, returning a value to the one the system calculated. */
  revert: UndoIcon,
  /** Marks a quoted value that differs from the requested one. */
  syncProblem: SyncProblemOutlinedIcon,

  // Domain / misc
  search: SearchIcon,
  comment: ChatBubbleOutlineIcon,
  /** A mail message or a mailbox. `comment` is an in-app note; this is email. */
  mail: MailOutlineIcon,
  /** Work set aside to finish later, such as unsent drafts. */
  pending: PendingActionsOutlinedIcon,
  /** Submit a message (e.g. a chat composer's send button). */
  send: SendIcon,
  /** AI-related affordance (extraction, suggestion, magic). */
  ai: AutoAwesomeOutlinedIcon,
  /** AI at work (e.g. a run in progress). Filled, to read as active beside `ai`. */
  aiWorking: AutoAwesomeIcon,
  drag: DragIndicatorIcon,
  settings: SettingsOutlinedIcon,
  person: PersonOutlinedIcon,
  group: GroupIcon,

  // Theme / mode
  lightMode: LightModeOutlinedIcon,
  darkMode: DarkModeOutlinedIcon,
} as const satisfies Record<string, ComponentType<SvgIconProps>>;

export type IconName = keyof typeof icons;

/** Ordered list of icon names. Exposed for Storybook/catalog rendering only. */
export const ICON_NAMES = Object.keys(icons) as IconName[];

export type IconSize = "sm" | "md" | "lg";

export type IconColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "error"
  | "success"
  | "warning"
  | "info"
  | "muted"
  | "disabled";

const sizeMap: Record<IconSize, SvgIconProps["fontSize"]> = {
  sm: "small",
  md: "medium",
  lg: "large",
};

const colorMap: Record<IconColor, SvgIconProps["color"]> = {
  inherit: "inherit",
  // This theme's `primary` is teal and `secondary` purple; mapping onto the
  // fixed brand names keeps those colours under the Kaizen theme too.
  primary: "brandSupporting",
  secondary: "brandPrimary",
  error: "error",
  success: "success",
  warning: "warning",
  info: "info",
  muted: "action",
  disabled: "disabled",
};

interface IconBaseProps {
  /** Semantic name from the design-system icon catalog. */
  name: IconName;
  /** Size token. Default: `"sm"` (matches the dense product surfaces where icons appear most). */
  size?: IconSize;
  /** Semantic color token. Default: `"inherit"` (follows surrounding text color). */
  color?: IconColor;
  /** Optional class name for layout-only styling. */
  className?: string;
  /** Native SVG title — surfaced as a tooltip by some browsers. */
  title?: string;
  /**
   * Accessible label.
   *
   * Pass this when the icon is the *only* thing conveying meaning (e.g. an
   * icon-only button without surrounding text). When omitted, the icon is
   * marked `aria-hidden` and treated as decorative — which is the right
   * default for the common case of an icon paired with adjacent text or
   * inside a labeled control like `IconButton`.
   */
  "aria-label"?: string;
}

export type IconProps = IconBaseProps;

/** Named icon from the design-system catalog. */
export const Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
  const {
    name,
    size = "sm",
    color = "inherit",
    className,
    title,
    "aria-label": ariaLabel,
  } = props;

  const isDecorative = ariaLabel === undefined;
  const Component = icons[name];
  return (
    <Component
      ref={ref}
      fontSize={sizeMap[size]}
      color={colorMap[color]}
      className={className}
      titleAccess={title}
      aria-label={ariaLabel}
      aria-hidden={isDecorative ? true : undefined}
      role={isDecorative ? "presentation" : "img"}
      focusable={false}
    />
  );
});

Icon.displayName = "Icon";

/**
 * Render a DS Icon into a detached DOM element.
 *
 * Useful for non-React surfaces that accept an `Element` (e.g. ag-grid's
 * `MenuItemDef.icon`). Creates a fresh container each call so the same
 * icon can appear in multiple menu items simultaneously.
 */
export function renderIconToElement(
  name: IconName,
  size: IconSize = "sm",
  color: IconColor = "inherit",
): HTMLElement {
  const container = document.createElement("span");
  container.style.display = "inline-flex";
  container.style.alignItems = "center";
  container.style.verticalAlign = "middle";
  // flushSync forces React to commit synchronously instead of scheduling
  // work via MessageChannel. Without it the scheduled task can fire after
  // a vitest test environment is torn down (window gone -> ReferenceError).
  flushSync(() => {
    createRoot(container).render(createElement(Icon, { name, size, color }));
  });
  return container;
}
